import type {
  OnChangeFn,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildFullColumnOrder,
  getDefaultColumnSettings,
  reconcileColumnSettings,
  type VorgangColumnSettings,
} from '@/pages/Vorgaenge/columns/columnMeta';

/**
 * Shared localStorage key for the column settings of both Vorgänge tables
 * (main list + archive). Bump the suffix when the shape of the persisted
 * payload changes in an incompatible way.
 */
const STORAGE_KEY = 'vorgang-columns:v1';

const hydrate = (): VorgangColumnSettings => {
  if (typeof window === 'undefined') return getDefaultColumnSettings();

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return getDefaultColumnSettings();
    const parsed = JSON.parse(stored) as Partial<VorgangColumnSettings>;
    return reconcileColumnSettings(parsed);
  } catch {
    // Corrupt JSON or storage unavailable: fall back to defaults.
    return getDefaultColumnSettings();
  }
};

/**
 * Persists column visibility, ordering, and sorting for the Vorgänge tables
 * in a single localStorage entry shared by the main list and the archive.
 *
 * - `columnOrder` / `columnVisibility` / `sorting` plug directly into
 *   TanStack Table's controlled state.
 * - `fullColumnOrder` is what callers pass to `useReactTable`: pinned ids
 *   (select, actions) are always prepended in their canonical order.
 * - `resetToDefault` clears storage and restores the original view.
 */
export const useVorgangColumnSettings = () => {
  const [settings, setSettings] = useState<VorgangColumnSettings>(() =>
    hydrate(),
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Silently fail if storage is unavailable (e.g. Safari private mode).
    }
  }, [settings]);

  const setColumnOrder = useCallback((next: string[]) => {
    setSettings((prev) => ({ ...prev, columnOrder: next }));
  }, []);

  const setColumnVisibility: OnChangeFn<VisibilityState> = useCallback(
    (updater) => {
      setSettings((prev) => ({
        ...prev,
        columnVisibility:
          typeof updater === 'function'
            ? updater(prev.columnVisibility)
            : updater,
      }));
    },
    [],
  );

  const setSorting: OnChangeFn<SortingState> = useCallback((updater) => {
    setSettings((prev) => ({
      ...prev,
      sorting: typeof updater === 'function' ? updater(prev.sorting) : updater,
    }));
  }, []);

  const resetToDefault = useCallback(() => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
    }
    setSettings(getDefaultColumnSettings());
  }, []);

  const fullColumnOrder = useMemo(
    () => buildFullColumnOrder(settings.columnOrder),
    [settings.columnOrder],
  );

  return {
    columnOrder: settings.columnOrder,
    columnVisibility: settings.columnVisibility,
    sorting: settings.sorting,
    fullColumnOrder,
    setColumnOrder,
    setColumnVisibility,
    setSorting,
    resetToDefault,
  };
};

export type UseVorgangColumnSettingsReturn = ReturnType<
  typeof useVorgangColumnSettings
>;
